import { trim, truncate } from '@/utils/lodash'

export const PROFORMA_DESCRIPTION_MAX_LENGTH = 300
/** Keep disclaimer short enough to stay on the financials page without orphaning a blank page. */
export const PROFORMA_DISCLAIMER_MAX_LENGTH = 450
/** A4 page bottom band reserved so absolute footer (~y:765) is not painted over. */
export const PROFORMA_FOOTER_BOTTOM_MARGIN = 90
/**
 * Description sits in the page-1 right column (left images are ~215 wide).
 * Keep a small inset only — a large right margin previously squeezed text into a
 * ~170pt strip, which wrapped heavily and page-broke mid-sentence.
 */
export const PROFORMA_DESCRIPTION_MARGIN = [0, 8, 8, 0]

const BULLET_LINE_RE = /^\s*(?:[-*•]|\d+[.)])\s+(.*)$/
const LIST_ITEM_ONLY_RE = /^\s*(?:[-*•]|\d+[.)])\s+/

/**
 * Prefer short proforma notes; never dump an uncapped full description into page 1.
 */
export function resolveProformaDescriptionText({
  proformaDescription = '',
  description = '',
} = {}) {
  const primary = trim(proformaDescription || '')
  const fallback = trim(description || '')
  const source = primary || fallback
  if (!source) return ''
  if (source.length <= PROFORMA_DESCRIPTION_MAX_LENGTH) return source
  return truncate(source, { length: PROFORMA_DESCRIPTION_MAX_LENGTH  })
}

/**
 * Existing pdfmake font-size heuristic based on length + newline count.
 */
export function getProformaDescriptionFontSize(text = '') {
  if (!text) return 14

  let lineWeight = text.split(/\r\n|\r|\n/).length
  if (lineWeight < 4) lineWeight *= 50
  else if (lineWeight < 8) lineWeight *= 75
  else lineWeight *= 100

  const score = text.length + lineWeight
  if (score > 1350) return 6.5
  if (score > 500) return 8
  if (score > 350) return 10
  if (score > 280) return 12
  return 14
}

/**
 * Split plain text into paragraphs and bullet/numbered lists.
 * Supports lines starting with -, *, •, or 1. / 1)
 */
export function parseDescriptionBlocks(text = '') {
  const normalized = String(text || '').replace(/\r\n|\r/g, '\n')
  if (!trim(normalized)) return []

  const lines = normalized.split('\n')
  const blocks = []
  let paragraphLines = []
  let listItems = []

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return
    const value = paragraphLines.join('\n').trimEnd()
    if (value) blocks.push({ type: 'paragraph', text: value  })
    paragraphLines = []
  }

  const flushList = () => {
    if (listItems.length === 0) return
    blocks.push({ type: 'list', items: listItems  })
    listItems = []
  }

  for (const line of lines) {
    const bulletMatch = line.match(BULLET_LINE_RE)
    if (bulletMatch) {
      flushParagraph()
      listItems.push(bulletMatch[1])
      continue
    }

    if (listItems.length > 0 && line.trim() === '') {
      flushList()
      continue
    }

    // Continuation of a list item (indented non-bullet line)
    if (
      listItems.length > 0 &&
      /^\s+/.test(line) &&
      line.trim() &&
      !LIST_ITEM_ONLY_RE.test(line)
    ) {
      listItems[listItems.length - 1] = `${listItems[listItems.length - 1]} ${line.trim()}`
      continue
    }

    flushList()

    if (line.trim() === '') {
      flushParagraph()
      continue
    }

    paragraphLines.push(line)
  }

  flushList()
  flushParagraph()
  return blocks
}

/**
 * pdfmake content for a property description (paragraphs + bullets).
 * Uses the full right-column width and stays on one page when possible.
 */
export function descriptionToPdfMakeContent(
  text,
  { fontSize = 14, margin = PROFORMA_DESCRIPTION_MARGIN } = {},
) {
  const blocks = parseDescriptionBlocks(text)
  if (blocks.length === 0) {
    return {
      text: '',
      margin,
      fontSize,
      width: '*',
      unbreakable: true,
    }
  }

  const stack = blocks.map((block, index) => {
    const blockMargin = [0, index === 0 ? 0 : 4, 0, 0]
    if (block.type === 'list') {
      return {
        ul: block.items,
        margin: blockMargin,
        fontSize,
      }
    }
    return {
      text: block.text,
      margin: blockMargin,
      fontSize,
    }
  })

  return {
    stack,
    margin,
    width: '*',
    // Prevent mid-description page breaks that orphan a word onto a blank page
    // and push Financial Projections down.
    unbreakable: true,
  }
}

export function buildProformaDescriptionPdfNode(
  property,
  { margin = PROFORMA_DESCRIPTION_MARGIN } = {},
) {
  const text = resolveProformaDescriptionText(property)
  const fontSize = getProformaDescriptionFontSize(text)
  return descriptionToPdfMakeContent(text, { fontSize, margin  })
}

/**
 * Truncate org legal disclaimer so it can sit under the page-2 tables
 * without spilling onto an empty following page.
 */
export function resolveProformaDisclaimerText(legalDescription = '') {
  const source = trim(legalDescription || '')
  if (!source) return ''
  if (source.length <= PROFORMA_DISCLAIMER_MAX_LENGTH) return source
  return truncate(source, { length: PROFORMA_DISCLAIMER_MAX_LENGTH  })
}

/**
 * pdfmake node for the proforma disclaimer, or null when empty.
 */
export function buildProformaDisclaimerPdfNode(legalDescription) {
  const text = resolveProformaDisclaimerText(legalDescription)
  if (!text) return null

  return {
    text,
    margin: [15, 4, 15, 2],
    color: 'gray',
    fontSize: text.length > 320 ? 6.5 : 7.5,
  }
}
